import { useCallback, useEffect, useMemo, useRef } from 'react';
import { TOPIC_NAMES } from 'crm-message-bus/types/MessageTopics';
import debounce from 'react-utils/debounce';
import { parseIdentifierKey } from './identifierKey';
import { useSendCrmMessageTopic } from 'crm-message-bus/useSendCrmMessageTopic';
/**
 * The purpose of this hook is to act as a messenger, notifying apps to update any stale data after property value(s) have been smart filled.
 * The hook also debounces the updates, so that if multiple updates are made in quick succession, they are only published once.
 */
export function useBatchUpdateObjects(debouncePeriod = 2000) {
  const pendingUpdatesRef = useRef(new Map());
  const sendUpdateObjectsMessage = useSendCrmMessageTopic(TOPIC_NAMES.UPDATE_OBJECTS);
  const flushUpdates = useCallback(() => {
    if (pendingUpdatesRef.current.size === 0) {
      return;
    }
    const updates = Array.from(pendingUpdatesRef.current.values()).map(({
      objectId,
      objectTypeId,
      propertyNames
    }) => ({
      objectId,
      objectTypeId,
      propertyNames: Array.from(propertyNames)
    }));
    sendUpdateObjectsMessage(updates);
    pendingUpdatesRef.current.clear();
  }, [sendUpdateObjectsMessage]);
  const debounceFlushUpdates = useMemo(() => {
    // eslint-disable-next-line react-hooks/refs
    return debounce(flushUpdates, debouncePeriod);
  }, [flushUpdates, debouncePeriod]);
  const queueUpdate = useCallback(key => {
    const {
      objectId,
      objectTypeId,
      propertyName
    } = parseIdentifierKey(key);
    const objectKey = `${objectTypeId}-${objectId}`;
    const existingUpdate = pendingUpdatesRef.current.get(objectKey);
    if (existingUpdate) {
      existingUpdate.propertyNames.add(propertyName);
    } else {
      pendingUpdatesRef.current.set(objectKey, {
        objectId,
        objectTypeId,
        propertyNames: new Set([propertyName])
      });
    }
    debounceFlushUpdates();
  }, [debounceFlushUpdates]);
  useEffect(() => {
    return () => {
      debounceFlushUpdates.reset();
      flushUpdates();
    };
  }, [debounceFlushUpdates, flushUpdates]);
  return {
    queueUpdate
  };
}